import React, { useState, useMemo } from 'react';
import { Layout, Menu, Badge } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShopOutlined,
  BulbOutlined,
  DatabaseOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  SwapOutlined,
  DollarOutlined,
  CloudUploadOutlined,
  ClearOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  TrophyOutlined,
  FilePdfOutlined,
  UserOutlined,
  SettingOutlined,
  ApartmentOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  AuditOutlined,
  SearchOutlined,
  LineChartOutlined,
  ImportOutlined,
  ShoppingCartOutlined,
  CreditCardOutlined,
  GlobalOutlined,
  WalletOutlined,
  BranchesOutlined,
  BellOutlined,
  ClockCircleOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { usePermissions } from '../contexts/PermissionContext';

const { Sider } = Layout;

const AppSidebar = ({ pendingApprovals = 0 }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { can, isAdmin, hasAnyRole, loading } = usePermissions();

  // Helper to check if user has permission for an item
  const hasPermission = (permission, roles, adminOnly) => {
    if (adminOnly && !isAdmin()) return false;
    if (roles && !hasAnyRole(roles)) return false;
    if (permission) {
      const [resource, action] = permission;
      return can(resource, action);
    }
    return true;
  };

  // Build the grouped menu structure
  const menuItems = useMemo(() => {
    if (loading) {
      return [{ key: '/', icon: <DashboardOutlined />, label: 'Dashboard' }];
    }

    const items = [];

    // Dashboard - standalone
    if (hasPermission(['dashboard', 'view'])) {
      items.push({
        key: '/',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      });
    }

    // Notifications - standalone
    items.push({
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    });

    // Data Import group
    const dataImportChildren = [];
    if (hasPermission(['spend', 'import'])) {
      dataImportChildren.push({
        key: '/spend-import',
        icon: <CloudUploadOutlined />,
        label: 'Spend Import',
      });
    }
    if (hasPermission(['data_cleansing', 'view'])) {
      dataImportChildren.push({
        key: '/data-cleansing',
        icon: <ClearOutlined />,
        label: 'Data Cleansing',
      });
    }
    if (hasPermission(['vendor_mapping', 'view'])) {
      dataImportChildren.push({
        key: '/vendor-mapping',
        icon: <BranchesOutlined />,
        label: 'Vendor Mapping',
      });
    }
    if (dataImportChildren.length > 0) {
      items.push({
        key: 'data-import',
        icon: <ImportOutlined />,
        label: 'Data Import',
        children: dataImportChildren,
      });
    }

    // My Vendors group
    const myVendorsChildren = [];
    if (hasPermission(['vendors', 'view'])) {
      myVendorsChildren.push({
        key: '/my-vendors',
        icon: <TeamOutlined />,
        label: 'Vendor List',
      });
    }
    if (hasPermission(['contracts', 'view'])) {
      myVendorsChildren.push({
        key: '/contracts',
        icon: <FileTextOutlined />,
        label: 'Contracts',
      });
      myVendorsChildren.push({
        key: '/renewals',
        icon: <ClockCircleOutlined />,
        label: 'Renewal Pipeline',
      });
    }
    if (hasPermission(['scorecards', 'view'])) {
      myVendorsChildren.push({
        key: '/scorecards',
        icon: <TrophyOutlined />,
        label: 'Scorecards',
      });
    }
    if (hasPermission(['vendors', 'view'])) {
      myVendorsChildren.push({
        key: '/rfp',
        icon: <FileSearchOutlined />,
        label: 'RFP Evaluation',
      });
    }
    if (myVendorsChildren.length > 0) {
      items.push({
        key: 'my-vendors-group',
        icon: <TeamOutlined />,
        label: 'My Vendors',
        children: myVendorsChildren,
      });
    }

    // Discover group
    const discoverChildren = [];
    if (hasPermission(['vendors', 'view'])) {
      discoverChildren.push({
        key: '/vendors',
        icon: <ShopOutlined />,
        label: 'All Vendors',
      });
      discoverChildren.push({
        key: '/product-database',
        icon: <DatabaseOutlined />,
        label: 'Product Database',
      });
      discoverChildren.push({
        key: '/compare',
        icon: <SwapOutlined />,
        label: 'Compare Vendors',
      });
    }
    if (hasPermission(['recommendations', 'view'])) {
      discoverChildren.push({
        key: '/alternatives',
        icon: <ThunderboltOutlined />,
        label: 'Alternatives',
      });
    }
    if (discoverChildren.length > 0) {
      items.push({
        key: 'discover',
        icon: <SearchOutlined />,
        label: 'Discover',
        children: discoverChildren,
      });
    }

    // Analytics group
    const analyticsChildren = [];
    if (hasPermission(['spend', 'view'])) {
      analyticsChildren.push({
        key: '/spend',
        icon: <DollarOutlined />,
        label: 'Spend Analytics',
      });
    }
    if (hasPermission(['recommendations', 'view'])) {
      analyticsChildren.push({
        key: '/recommendations',
        icon: <BulbOutlined />,
        label: 'Consolidation',
      });
    }
    if (hasPermission(['reports', 'view'])) {
      analyticsChildren.push({
        key: '/reports',
        icon: <FilePdfOutlined />,
        label: 'Reports',
      });
    }
    // Forecasting - visible to anyone who can view spend
    if (hasPermission(['spend', 'view'])) {
      analyticsChildren.push({
        key: '/forecasting',
        icon: <LineChartOutlined />,
        label: 'Forecasting',
      });
    }
    // Cost Centers - visible to anyone who can view spend
    if (hasPermission(['spend', 'view'])) {
      analyticsChildren.push({
        key: '/cost-centers',
        icon: <WalletOutlined />,
        label: 'Cost Centers',
      });
    }
    // Market Intelligence - visible to anyone who can view vendors
    if (hasPermission(['vendors', 'view'])) {
      analyticsChildren.push({
        key: '/market-intelligence',
        icon: <GlobalOutlined />,
        label: 'Market Intelligence',
      });
    }
    // Supplier Diversity - visible to anyone who can view vendors
    if (hasPermission(['vendors', 'view'])) {
      analyticsChildren.push({
        key: '/supplier-diversity',
        icon: <TeamOutlined />,
        label: 'Supplier Diversity',
      });
    }
    // Savings Levers - visible to anyone who can view spend
    if (hasPermission(['spend', 'view'])) {
      analyticsChildren.push({
        key: '/savings',
        icon: <DollarOutlined />,
        label: 'Savings Levers',
      });
    }
    // Tasks - visible to anyone who can view recommendations
    if (hasPermission(['recommendations', 'view'])) {
      analyticsChildren.push({
        key: '/tasks',
        icon: <CheckCircleOutlined />,
        label: 'Tasks',
      });
    }
    if (analyticsChildren.length > 0) {
      items.push({
        key: 'analytics',
        icon: <LineChartOutlined />,
        label: 'Analytics',
        children: analyticsChildren,
      });
    }

    // Accounts Payable group (Phase 5.1)
    const apChildren = [];
    if (hasPermission(['invoices', 'view'])) {
      apChildren.push({
        key: '/purchase-orders',
        icon: <ShoppingCartOutlined />,
        label: 'Purchase Orders',
      });
      apChildren.push({
        key: '/invoices',
        icon: <FileTextOutlined />,
        label: 'Invoices',
      });
    }
    if (apChildren.length > 0) {
      items.push({
        key: 'accounts-payable',
        icon: <CreditCardOutlined />,
        label: 'Accounts Payable',
        children: apChildren,
      });
    }

    // Admin group
    const adminChildren = [];
    if (hasPermission(['approvals', 'view'], ['admin', 'manager', 'finance'])) {
      adminChildren.push({
        key: '/approvals',
        icon: pendingApprovals > 0 ? (
          <Badge count={pendingApprovals} size="small">
            <CheckCircleOutlined style={{ color: 'inherit' }} />
          </Badge>
        ) : (
          <CheckCircleOutlined />
        ),
        label: 'Approvals',
      });
    }
    if (hasPermission(['users', 'view'], null, true)) {
      adminChildren.push({
        key: '/users',
        icon: <UserOutlined />,
        label: 'Users',
      });
    }
    if (hasPermission(['departments', 'edit'], null, true)) {
      adminChildren.push({
        key: '/departments',
        icon: <ApartmentOutlined />,
        label: 'Departments',
      });
    }
    if (hasPermission(['organization', 'view'], null, true)) {
      adminChildren.push({
        key: '/settings',
        icon: <SettingOutlined />,
        label: 'Organization',
      });
    }
    if (hasPermission(null, null, false)) {
      adminChildren.push({
        key: '/security',
        icon: <SafetyCertificateOutlined />,
        label: 'Security',
      });
    }
    if (hasPermission(['audit', 'view'], null, true)) {
      adminChildren.push({
        key: '/audit',
        icon: <AuditOutlined />,
        label: 'Audit Logs',
      });
    }
    if (adminChildren.length > 0) {
      items.push({
        key: 'admin',
        icon: <SettingOutlined />,
        label: 'Admin',
        children: adminChildren,
      });
    }

    return items;
  }, [loading, can, isAdmin, hasAnyRole, pendingApprovals]);

  // Determine which submenu should be open based on current path
  const getOpenKeys = () => {
    const path = location.pathname;
    if (['/spend-import', '/data-cleansing', '/vendor-mapping'].includes(path)) return ['data-import'];
    if (['/my-vendors', '/contracts', '/renewals', '/scorecards', '/rfp'].includes(path) || path.startsWith('/rfp/')) return ['my-vendors-group'];
    if (['/vendors', '/product-database', '/compare', '/alternatives'].includes(path)) return ['discover'];
    if (['/spend', '/recommendations', '/reports', '/forecasting', '/market-intelligence', '/cost-centers', '/supplier-diversity', '/savings', '/tasks'].includes(path)) return ['analytics'];
    if (['/purchase-orders', '/invoices'].includes(path) || path.startsWith('/invoices/')) return ['accounts-payable'];
    if (['/approvals', '/users', '/departments', '/settings', '/security', '/audit'].includes(path)) return ['admin'];
    return [];
  };

  const [openKeys, setOpenKeys] = useState(getOpenKeys());

  // Update open keys when location changes
  React.useEffect(() => {
    const newOpenKeys = getOpenKeys();
    if (newOpenKeys.length > 0 && !openKeys.includes(newOpenKeys[0])) {
      setOpenKeys(prev => [...new Set([...prev, ...newOpenKeys])]);
    }
  }, [location.pathname]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
    >
      <div className="app-logo" />
      <div style={{ paddingBottom: 96 }}>
        <Menu
          theme="dark"
          selectedKeys={[location.pathname]}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          mode="inline"
          items={menuItems}
          onClick={({ key }) => {
            if (!key.startsWith('data-import') && !key.startsWith('my-vendors-group') &&
                !key.startsWith('discover') && !key.startsWith('analytics') &&
                !key.startsWith('accounts-payable') && !key.startsWith('admin')) {
              navigate(key);
            }
          }}
        />
      </div>
      <div
        style={{
          position: 'fixed',
          bottom: 48,
          left: 0,
          width: collapsed ? 80 : 200,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          padding: collapsed ? '12px 0' : '12px 24px',
          cursor: 'pointer',
          color: location.pathname === '/user-guide' ? '#1890ff' : 'rgba(255,255,255,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10,
          fontSize: 14,
          background: '#001529',
          zIndex: 10,
          transition: 'width 0.2s',
        }}
        onClick={() => navigate('/user-guide')}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={(e) => {
          if (location.pathname !== '/user-guide') {
            e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
          }
        }}
      >
        <BookOutlined />
        {!collapsed && <span>User Guide</span>}
      </div>
    </Sider>
  );
};

export default AppSidebar;